import React, { useState, useEffect } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { format, subDays, subWeeks, subMonths, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isValid } from 'date-fns';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface TicketVolumeDataItem {
  date: string;
  tickets: number;
  period?: string; // Keep original period for sorting
}

interface TicketVolumeResponse {
  timeRange: string;
  data: Array<{
    period: string;
    total: number;
    new: number;
    inProgress: number;
    resolved: number;
    closed: number;
  }>;
  summary: {
    totalTickets: number;
    averagePerPeriod: number;
    periods: number;
  };
}

interface DeviceModelDataItem {
  name: string;
  value: number;
  color: string | null;
}

interface CategoryDataItem {
  name: string;
  value: number;
  color: string;
}

interface GeographyDataItem {
  country: string;
  count: number;
}

interface GeographyResponse {
  countries: Array<{
    country: string;
    total: number;
    states: Array<{
      state: string;
      total: number;
      cities: Array<{
        city: string;
        total: number;
      }>;
    }>;
  }>;
  summary: {
    totalCountries: number;
    totalStates: number;
    totalCities: number;
  };
}

interface AgentActivityDataItem {
  agentId: string;
  name: string;
  date: string;
  hoursWorked: number;
  avgDailyHours: number;
}

interface AgentActivityResponse {
  agents: Array<AgentActivityDataItem>;
  summary: {
    totalAgents: number;
    activeAgents: number;
    avgHoursPerAgent: number;
  };
}

interface HourlyTicketDataItem {
  hour: number;
  hourLabel: string;
  count: number;
  percentage: number;
}

interface HourlyTicketResponse {
  data: Array<HourlyTicketDataItem>;
  statistics: {
    totalTickets: number;
    peakHour: {
      hour: number;
      label: string;
      count: number;
      percentage: number;
    };
    businessHours: {
      range: string;
      count: number;
      percentage: number;
    };
    afterHours: {
      range: string;
      count: number;
      percentage: number;
    };
    quietHours: number;
  };
}

interface ServiceWorkflowVolumeDataItem {
  period: string;
  total: number;
  completed: number;
  in_progress: number;
  cancelled: number;
  completion_rate: number;
}

interface ServiceWorkflowVolumeResponse {
  timeRange: string;
  data: Array<ServiceWorkflowVolumeDataItem>;
  summary: {
    totalWorkflows: number;
    completed: number;
    inProgress: number;
    cancelled: number;
    completionRate: number;
  };
}

interface TopReplacedComponentDataItem {
  partNumber: string;
  itemName: string;
  category: string;
  description?: string;
  count: number;
  workflowCount: number;
}

interface TopReplacedComponentsResponse {
  data: Array<TopReplacedComponentDataItem>;
  summary: {
    totalWorkflows: number;
    totalPartsReplaced: number;
    uniqueParts: number;
    topPartUsage: number;
    averagePartsPerWorkflow: number;
  };
  categoryBreakdown: {
    [category: string]: {
      count: number;
      parts: number;
    };
  };
}

interface InsightsData {
  ticketVolumeData: Array<TicketVolumeDataItem> | TicketVolumeResponse;
  geographyData: Array<GeographyDataItem> | GeographyResponse;
  topAgents: Array<{ 
    id: string; 
    name: string; 
    ticketsResolved: number; 
  avgResolutionTime: number; // in minutes, rounded up
  avgResolutionTimeHours?: number; // in hours, for reference
  avgResolutionTimeMinutes?: number; // in minutes, rounded up
    totalActiveTime: number;
  }>;
  categoryData: Array<CategoryDataItem>;
  deviceModelData: Array<DeviceModelDataItem>;
  resolutionMetrics: {
    avgResolutionTime: number;
    minResolutionTime: number;
    maxResolutionTime: number;
  };
  agentActivityData: Array<AgentActivityDataItem> | AgentActivityResponse;
  unresolvedTickets: {
    count: number;
    overdueCount: number;
    avgAge: number;
    tickets: Array<{ id: string; title: string; daysOpen: number }>;
  };
  ticketFlowData: Array<{ stage: string; count: number; color: string }>;
  hourlyTicketData: HourlyTicketResponse;
  serviceWorkflowVolumeData: ServiceWorkflowVolumeResponse;
  topReplacedComponentsData: TopReplacedComponentsResponse;
}

type DateRangeOption = 'last_7_days' | 'last_30_days' | 'last_week' | 'last_month' | 'last_3_months' | 'last_6_months' | 'custom';

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const Insights: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRangeOption, setDateRangeOption] = useState<DateRangeOption>('last_30_days');
  const [customDateRange, setCustomDateRange] = useState<DateRange>({ startDate: null, endDate: null });
  const [selectedAgent, setSelectedAgent] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [data, setData] = useState<InsightsData | null>(null);
  
  // Auto-refresh state
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [nextRefreshIn, setNextRefreshIn] = useState(30);

  // Calculate date range based on selected option
  const getDateRange = (): DateRange => {
    const now = new Date();
    
    switch (dateRangeOption) {
      case 'last_7_days':
        return {
          startDate: subDays(now, 7),
          endDate: now
        };
      case 'last_30_days':
        return {
          startDate: subDays(now, 30),
          endDate: now
        };
      case 'last_week':
        const lastWeekStart = startOfWeek(subWeeks(now, 1));
        const lastWeekEnd = endOfWeek(subWeeks(now, 1));
        return {
          startDate: lastWeekStart,
          endDate: lastWeekEnd
        };
      case 'last_month':
        const lastMonthStart = startOfMonth(subMonths(now, 1));
        const lastMonthEnd = endOfMonth(subMonths(now, 1));
        return {
          startDate: lastMonthStart,
          endDate: lastMonthEnd
        };
      case 'last_3_months':
        return {
          startDate: subMonths(now, 3),
          endDate: now
        };
      case 'last_6_months':
        return {
          startDate: subMonths(now, 6),
          endDate: now
        };
      case 'custom':
        return customDateRange;
      default:
        return {
          startDate: subDays(now, 30),
          endDate: now
        };
    }
  };

  // Effect for filter changes
  useEffect(() => {
    loadInsightsData();
  }, [dateRangeOption, customDateRange, selectedAgent, selectedCategory]);

  // Auto-refresh timer
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    
    if (autoRefreshEnabled && refreshInterval > 0) {
      console.log('📊 AUTO-REFRESH: Setting up auto-refresh every', refreshInterval, 'seconds');
      
      intervalId = setInterval(() => {
        console.log('📊 AUTO-REFRESH: Refreshing insights data...');
        loadInsightsData();
        setLastRefresh(new Date());
        setNextRefreshIn(refreshInterval);
      }, refreshInterval * 1000);
    }

    return () => {
      if (intervalId) {
        console.log('📊 AUTO-REFRESH: Clearing auto-refresh interval');
        clearInterval(intervalId);
      }
    };
  }, [autoRefreshEnabled, refreshInterval]);

  // Countdown timer for "next refresh in X seconds"
  useEffect(() => {
    let countdownId: NodeJS.Timeout;
    
    if (autoRefreshEnabled && refreshInterval > 0) {
      countdownId = setInterval(() => {
        setNextRefreshIn(prev => {
          const next = prev - 1;
          return next <= 0 ? refreshInterval : next;
        });
      }, 1000);
    }

    return () => {
      if (countdownId) {
        clearInterval(countdownId);
      }
    };
  }, [autoRefreshEnabled, refreshInterval]);

  const loadInsightsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const dateRange = getDateRange();
      
      // Skip loading if custom date range is incomplete
      if (dateRangeOption === 'custom' && (!dateRange.startDate || !dateRange.endDate)) {
        setLoading(false);
        return;
      }
      
      console.log('🔍 INSIGHTS: Loading insights data with date range:', dateRange);
      
      // Determine appropriate timeRange based on date range
      const getTimeRange = (): 'daily' | 'weekly' | 'monthly' | 'yearly' => {
        if (!dateRange.startDate || !dateRange.endDate) return 'monthly';
        
        const diffDays = Math.abs((dateRange.endDate.getTime() - dateRange.startDate.getTime()) / (1000 * 60 * 60 * 24));
        
        if (diffDays <= 30) return 'daily';
        if (diffDays <= 90) return 'weekly';
        if (diffDays <= 365) return 'monthly';
        return 'yearly';
      };

      const timeRange = getTimeRange();
      console.log('🔍 INSIGHTS: Selected date range:', dateRange);
      console.log('🔍 INSIGHTS: Calculated timeRange:', timeRange);
      
      const insights = await apiService.getInsights({
        timeRange,
        startDate: dateRange.startDate ? format(dateRange.startDate, 'yyyy-MM-dd') : undefined,
        endDate: dateRange.endDate ? format(dateRange.endDate, 'yyyy-MM-dd') : undefined,
        agentId: selectedAgent === 'all' ? undefined : selectedAgent,
        category: selectedCategory === 'all' ? undefined : selectedCategory
      });
      
      console.log('🔍 INSIGHTS: Received insights data:', insights);
      console.log('🔍 INSIGHTS: Device model data:', insights.deviceModelData);
      
      setData(insights);
      setLastRefresh(new Date());
      setNextRefreshIn(refreshInterval);
    } catch (err) {
      console.error('Failed to load insights data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes: number): string => {
    // Round up the total minutes first
    const totalMinutes = Math.ceil(minutes);
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDuration = (hours: number): string => {
    return `${hours.toFixed(1)}h`;
  };

  const handleDateRangeChange = (option: DateRangeOption) => {
    setDateRangeOption(option);
    if (option !== 'custom') {
      setCustomDateRange({ startDate: null, endDate: null });
    }
  };

  const handleCustomDateChange = (field: 'startDate' | 'endDate', dateString: string) => {
    const date = dateString ? new Date(dateString) : null;
    setCustomDateRange(prev => ({
      ...prev,
      [field]: date && isValid(date) ? date : null
    }));
  };

  const formatDateForInput = (date: Date | null): string => {
    if (!date || !isValid(date)) return '';
    return format(date, 'yyyy-MM-dd');
  };

  const getDateRangeLabel = (): string => {
    const range = getDateRange();
    if (!range.startDate || !range.endDate) return 'Select date range';
    
    return `${format(range.startDate, 'MMM dd, yyyy')} - ${format(range.endDate, 'MMM dd, yyyy')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error Loading Insights</h3>
            <div className="mt-2 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data && dateRangeOption === 'custom' && (!customDateRange.startDate || !customDateRange.endDate)) {
    return (
      <div className="space-y-6">
        {/* Header with Date Range Selection */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Analytics Dashboard</h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Comprehensive insights into ticket metrics and agent performance
              </p>
            </div>
          </div>

          {/* Date Range Selection */}
          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date Range
              </label>
              <select
                value={dateRangeOption}
                onChange={(e) => handleDateRangeChange(e.target.value as DateRangeOption)}
                className="block w-full sm:w-auto rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="last_7_days">Last 7 Days</option>
                <option value="last_30_days">Last 30 Days</option>
                <option value="last_week">Last Week</option>
                <option value="last_month">Last Month</option>
                <option value="last_3_months">Last 3 Months</option>
                <option value="last_6_months">Last 6 Months</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {dateRangeOption === 'custom' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formatDateForInput(customDateRange.startDate)}
                    onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formatDateForInput(customDateRange.endDate)}
                    onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
              </div>
            )}

            {dateRangeOption !== 'custom' && (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium">Selected Range:</span> {getDateRangeLabel()}
              </div>
            )}
          </div>
        </div>

        <div className="text-center text-gray-500 dark:text-gray-400 py-8">
          Please select both start and end dates for custom range
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center text-gray-500 dark:text-gray-400 py-8">
        No insights data available
      </div>
    );
  }

  // Helper function to format period for display
  const formatPeriodForDisplay = (period: string, timeRange: string): string => {
    try {
      if (timeRange === 'daily') {
        // period format: "2025-07-15"
        const date = new Date(period);
        return format(date, 'MMM dd');
      } else if (timeRange === 'weekly') {
        // period format: "2025-07-14" (week start)
        const date = new Date(period);
        return format(date, 'MMM dd');
      } else if (timeRange === 'monthly') {
        // period format: "2025-07"
        const [year, month] = period.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        return format(date, 'MMM yyyy');
      } else if (timeRange === 'yearly') {
        // period format: "2025"
        return period;
      }
      return period;
    } catch (error) {
      console.warn('Error formatting period:', period, error);
      return period;
    }
  };

  // Ensure all data properties have default values
  const transformTicketVolumeData = (ticketVolumeData: Array<TicketVolumeDataItem> | TicketVolumeResponse | undefined): Array<TicketVolumeDataItem> => {
    console.log('🔍 TRANSFORM: Raw ticketVolumeData:', ticketVolumeData);
    console.log('🔍 TRANSFORM: Type of ticketVolumeData:', typeof ticketVolumeData);
    console.log('🔍 TRANSFORM: Is Array:', Array.isArray(ticketVolumeData));
    
    if (!ticketVolumeData) {
      console.log('🔍 TRANSFORM: No ticket volume data, returning empty array');
      return [];
    }
    
    // If it's already an array, return it
    if (Array.isArray(ticketVolumeData)) {
      console.log('🔍 TRANSFORM: Data is already an array, returning as-is');
      return ticketVolumeData;
    }
    
    // If it's an object with data property, transform it
    if (ticketVolumeData.data) {
      console.log('🔍 TRANSFORM: Found data property, transforming...');
      console.log('🔍 TRANSFORM: ticketVolumeData.data:', ticketVolumeData.data);
      console.log('🔍 TRANSFORM: timeRange:', ticketVolumeData.timeRange);
      
      const transformed = ticketVolumeData.data.map(item => ({
        date: formatPeriodForDisplay(item.period, ticketVolumeData.timeRange || 'monthly'),
        tickets: item.total,
        period: item.period // Keep original period for sorting
      }));
      console.log('🔍 TRANSFORM: Transformed data:', transformed);
      return transformed;
    }
    
    console.log('🔍 TRANSFORM: No matching condition, returning empty array');
    return [];
  };

  const transformGeographyData = (geographyData: Array<GeographyDataItem> | GeographyResponse | undefined): Array<GeographyDataItem> => {
    console.log('🌍 GEOGRAPHY: Raw geography data:', geographyData);
    console.log('🌍 GEOGRAPHY: Type:', typeof geographyData);
    console.log('🌍 GEOGRAPHY: Is Array:', Array.isArray(geographyData));
    
    if (!geographyData) {
      console.log('🌍 GEOGRAPHY: No geography data received');
      return [];
    }
    
    // If it's already an array, return it
    if (Array.isArray(geographyData)) {
      console.log('🌍 GEOGRAPHY: Data is already an array');
      return geographyData;
    }
    
    // If it's an object with countries property, transform it
    if (geographyData.countries) {
      console.log('🌍 GEOGRAPHY: Found countries property:', geographyData.countries);
      const transformed = geographyData.countries.map(country => ({
        country: country.country,
        count: country.total
      }));
      console.log('🌍 GEOGRAPHY: Transformed data:', transformed);
      return transformed;
    }
    
    console.log('🌍 GEOGRAPHY: No matching format, returning empty array');
    return [];
  };

  const transformAgentActivityData = (agentActivityData: Array<AgentActivityDataItem> | AgentActivityResponse | undefined): Array<AgentActivityDataItem> => {
    console.log('🕒 TRANSFORM: Raw agent activity data:', agentActivityData);
    console.log('🕒 TRANSFORM: Type:', typeof agentActivityData);
    console.log('🕒 TRANSFORM: Is Array:', Array.isArray(agentActivityData));
    
    if (!agentActivityData) {
      console.log('🕒 TRANSFORM: No agent activity data received');
      return [];
    }
    
    // If it's already an array, return it
    if (Array.isArray(agentActivityData)) {
      console.log('🕒 TRANSFORM: Data is already an array with', agentActivityData.length, 'items');
      return agentActivityData;
    }
    
    // If it's an object with agents property, transform it
    if (agentActivityData.agents) {
      console.log('🕒 TRANSFORM: Found agents property with', agentActivityData.agents.length, 'agents');
      return agentActivityData.agents;
    }
    
    console.log('🕒 TRANSFORM: No matching format, returning empty array');
    return [];
  };

  const transformServiceWorkflowVolumeData = (serviceWorkflowData: ServiceWorkflowVolumeResponse | undefined): Array<ServiceWorkflowVolumeDataItem & { displayPeriod: string }> => {
    console.log('🔧 TRANSFORM: Raw service workflow data:', serviceWorkflowData);
    
    if (!serviceWorkflowData || !serviceWorkflowData.data) {
      console.log('🔧 TRANSFORM: No service workflow data, returning empty array');
      return [];
    }
    
    console.log('🔧 TRANSFORM: Processing', serviceWorkflowData.data.length, 'service workflow periods');
    
    // Format period dates for display
    const transformed = serviceWorkflowData.data.map(item => {
      const formatPeriodForDisplay = (period: string, timeRange: string): string => {
        try {
          const date = new Date(period);
          if (timeRange === 'daily') {
            return format(date, 'MMM dd');
          } else if (timeRange === 'weekly') {
            return format(date, 'MMM dd');
          } else if (timeRange === 'monthly') {
            return format(date, 'MMM yyyy');
          } else if (timeRange === 'yearly') {
            return format(date, 'yyyy');
          }
          return period;
        } catch (error) {
          console.error('🔧 TRANSFORM: Error formatting period:', period, error);
          return period;
        }
      };

      return {
        ...item,
        displayPeriod: formatPeriodForDisplay(item.period, serviceWorkflowData.timeRange || 'monthly')
      };
    });
    
    console.log('🔧 TRANSFORM: Transformed service workflow data:', transformed.slice(0, 3));
    return transformed;
  };

  const processedAgentActivityData = transformAgentActivityData(data.agentActivityData);
  const transformedServiceWorkflowData = transformServiceWorkflowVolumeData(data.serviceWorkflowVolumeData);

  const safeData = {
    ticketVolumeData: transformTicketVolumeData(data.ticketVolumeData),
    geographyData: transformGeographyData(data.geographyData),
    topAgents: data.topAgents || [],
    categoryData: data.categoryData || [],
    deviceModelData: data.deviceModelData || [],
    resolutionMetrics: data.resolutionMetrics || {
      avgResolutionTime: 0,
      minResolutionTime: 0,
      maxResolutionTime: 0
    },
    agentActivityData: processedAgentActivityData,
    unresolvedTickets: data.unresolvedTickets || {
      count: 0,
      overdueCount: 0,
      avgAge: 0,
      tickets: []
    },
    ticketFlowData: data.ticketFlowData || [],
    hourlyTicketData: data.hourlyTicketData || {
      data: Array.from({ length: 24 }, (_, hour) => ({
        hour: hour,
        hourLabel: `${hour.toString().padStart(2, '0')}:00`,
        count: 0,
        percentage: 0
      })),
      statistics: {
        totalTickets: 0,
        peakHour: { hour: 9, label: "09:00", count: 0, percentage: 0 },
        businessHours: { range: "09:00-17:00", count: 0, percentage: 0 },
        afterHours: { range: "17:00-09:00", count: 0, percentage: 0 },
        quietHours: 24
      }
    },
    serviceWorkflowVolumeData: transformedServiceWorkflowData,
    topReplacedComponentsData: data.topReplacedComponentsData || {
      data: [],
      summary: {
        totalWorkflows: 0,
        totalPartsReplaced: 0,
        uniqueParts: 0,
        topPartUsage: 0,
        averagePartsPerWorkflow: 0
      },
      categoryBreakdown: {}
    }
  };

  console.log('🍕 PIE CHART AUDIT: Raw data from API:', {
    categoryData: data.categoryData,
    deviceModelData: data.deviceModelData,
    categoryDataType: typeof data.categoryData,
    deviceModelDataType: typeof data.deviceModelData,
    categoryIsArray: Array.isArray(data.categoryData),
    deviceModelIsArray: Array.isArray(data.deviceModelData)
  });

  console.log('🔍 CHART: Final data being passed to LineChart:', safeData.ticketVolumeData);
  console.log('🔍 CHART: Data length:', safeData.ticketVolumeData.length);
  console.log('🔍 CHART: Sample data item:', safeData.ticketVolumeData[0]);
  
  console.log('🌍 GEOGRAPHY: Final geography data for chart:', safeData.geographyData);
  console.log('🌍 GEOGRAPHY: Geography data length:', safeData.geographyData.length);
  
  console.log('🍕 PIE CHART FRONTEND: Category data for pie chart:', safeData.categoryData);
  console.log('🍕 PIE CHART FRONTEND: Device model data for pie chart:', safeData.deviceModelData);
  console.log('🍕 PIE CHART FRONTEND: Category data length:', safeData.categoryData.length);
  console.log('🍕 PIE CHART FRONTEND: Device model data length:', safeData.deviceModelData.length);
  
  console.log('🏆 TOP AGENTS FRONTEND: Raw top agents data:', data.topAgents);
  console.log('🏆 TOP AGENTS FRONTEND: Processed top agents data:', safeData.topAgents);
  console.log('🏆 TOP AGENTS FRONTEND: Sample agent data:', safeData.topAgents[0]);
  
  console.log('🕒 ACTIVITY FRONTEND: Raw agent activity data:', data.agentActivityData);
  console.log('🕒 ACTIVITY FRONTEND: Processed agent activity data:', processedAgentActivityData);
  console.log('🕒 ACTIVITY FRONTEND: Final agent activity data:', safeData.agentActivityData);
  console.log('🕒 ACTIVITY FRONTEND: Final data length:', safeData.agentActivityData.length);
  console.log('🕒 ACTIVITY FRONTEND: Sample activity data:', safeData.agentActivityData[0]);
  
  console.log('⏱️ RESOLUTION FRONTEND: Raw resolution metrics:', data.resolutionMetrics);
  console.log('⏱️ RESOLUTION FRONTEND: Final resolution metrics:', safeData.resolutionMetrics);
  console.log('⏱️ RESOLUTION FRONTEND: Avg time:', safeData.resolutionMetrics.avgResolutionTime);
  console.log('⏱️ RESOLUTION FRONTEND: Min time:', safeData.resolutionMetrics.minResolutionTime);
  console.log('⏱️ RESOLUTION FRONTEND: Max time:', safeData.resolutionMetrics.maxResolutionTime);
  
  console.log('🎫 UNRESOLVED FRONTEND: Raw unresolved tickets:', data.unresolvedTickets);
  console.log('🎫 UNRESOLVED FRONTEND: Final unresolved tickets:', safeData.unresolvedTickets);
  console.log('🎫 UNRESOLVED FRONTEND: Count:', safeData.unresolvedTickets.count);
  console.log('🎫 UNRESOLVED FRONTEND: Overdue count:', safeData.unresolvedTickets.overdueCount);
  console.log('🎫 UNRESOLVED FRONTEND: Avg age:', safeData.unresolvedTickets.avgAge, 'days');
  
  console.log('🏆 TOP AGENTS FRONTEND DETAILED: Raw data length:', data.topAgents?.length || 0);
  console.log('🏆 TOP AGENTS FRONTEND DETAILED: First agent raw:', data.topAgents?.[0]);
  console.log('🏆 TOP AGENTS FRONTEND DETAILED: SafeData length:', safeData.topAgents.length);
  console.log('🏆 TOP AGENTS FRONTEND DETAILED: First agent safe:', safeData.topAgents[0]);
  
  console.log('🕒 HOURLY FRONTEND: Raw hourly ticket data:', data.hourlyTicketData);
  console.log('🕒 HOURLY FRONTEND: Final hourly ticket data:', safeData.hourlyTicketData);
  console.log('🕒 HOURLY FRONTEND: Statistics:', safeData.hourlyTicketData.statistics);
  console.log('🕒 HOURLY FRONTEND: Peak hour:', safeData.hourlyTicketData.statistics.peakHour.label, 'with', safeData.hourlyTicketData.statistics.peakHour.count, 'tickets');
  
  console.log('🔧 SERVICE WORKFLOW FRONTEND: Raw data:', data.serviceWorkflowVolumeData);
  console.log('🔧 SERVICE WORKFLOW FRONTEND: Transformed data:', safeData.serviceWorkflowVolumeData);
  console.log('🔧 SERVICE WORKFLOW FRONTEND: Data length:', safeData.serviceWorkflowVolumeData.length);
  console.log('🔧 SERVICE WORKFLOW FRONTEND: First item:', safeData.serviceWorkflowVolumeData[0]);
  
  console.log('🔩 TOP COMPONENTS FRONTEND: Raw data:', data.topReplacedComponentsData);
  console.log('🔩 TOP COMPONENTS FRONTEND: Final data:', safeData.topReplacedComponentsData);
  console.log('🔩 TOP COMPONENTS FRONTEND: Components count:', safeData.topReplacedComponentsData.data.length);
  console.log('🔩 TOP COMPONENTS FRONTEND: Summary:', safeData.topReplacedComponentsData.summary);
  console.log('🔩 TOP COMPONENTS FRONTEND: Top component:', safeData.topReplacedComponentsData.data[0]);

  return (
    <div className="space-y-6">
      {/* Header with Enhanced Date Range Selection */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Analytics Dashboard</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Comprehensive insights into ticket metrics and agent performance
            </p>
          </div>
        </div>

        {/* Enhanced Date Range and Filters */}
        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Date Range Selection */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date Range
                </label>
                <select
                  value={dateRangeOption}
                  onChange={(e) => handleDateRangeChange(e.target.value as DateRangeOption)}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value="last_7_days">Last 7 Days</option>
                  <option value="last_30_days">Last 30 Days</option>
                  <option value="last_week">Last Week</option>
                  <option value="last_month">Last Month</option>
                  <option value="last_3_months">Last 3 Months</option>
                  <option value="last_6_months">Last 6 Months</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {dateRangeOption === 'custom' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={formatDateForInput(customDateRange.startDate)}
                      onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                      className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={formatDateForInput(customDateRange.endDate)}
                      onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                      className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    />
                  </div>
                </div>
              )}

              <div className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium">Selected Range:</span> {getDateRangeLabel()}
              </div>
            </div>

            {/* Other Filters */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Filters
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <select
                    value={selectedAgent}
                    onChange={(e) => setSelectedAgent(e.target.value)}
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                  >
                    <option value="all">All Agents</option>
                    {safeData.topAgents.map(agent => (
                      <option key={agent.id} value={agent.id}>{agent.name}</option>
                    ))}
                  </select>
                  
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                  >
                    <option value="all">All Categories</option>
                    {safeData.categoryData.map(category => (
                      <option key={category.name} value={category.name}>{category.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Auto-Refresh Controls */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Auto-Refresh
                </label>
                <div className="space-y-3">
                  {/* Auto-refresh toggle */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${autoRefreshEnabled ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`}></div>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {autoRefreshEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <button
                      onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        autoRefreshEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          autoRefreshEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Refresh interval selector */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Refresh Every
                    </label>
                    <select
                      value={refreshInterval}
                      onChange={(e) => setRefreshInterval(Number(e.target.value))}
                      disabled={!autoRefreshEnabled}
                      className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm disabled:opacity-50"
                    >
                      <option value={15}>15 seconds</option>
                      <option value={30}>30 seconds</option>
                      <option value={60}>1 minute</option>
                      <option value={120}>2 minutes</option>
                      <option value={300}>5 minutes</option>
                    </select>
                  </div>

                  {/* Status display */}
                  <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                    <div>
                      <span className="font-medium">Last Updated:</span> {format(lastRefresh, 'HH:mm:ss')}
                    </div>
                    {autoRefreshEnabled && (
                      <div>
                        <span className="font-medium">Next Refresh:</span> {nextRefreshIn}s
                      </div>
                    )}
                  </div>

                  {/* Manual refresh button */}
                  <button
                    onClick={() => {
                      loadInsightsData();
                      setLastRefresh(new Date());
                      setNextRefreshIn(refreshInterval);
                    }}
                    disabled={loading}
                    className="w-full flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh Now
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Total Tickets
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {safeData.ticketVolumeData.reduce((sum: number, item: TicketVolumeDataItem) => sum + item.tickets, 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Avg Resolution Time
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {formatTime(safeData.resolutionMetrics.avgResolutionTime)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 14.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Unresolved Tickets
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {safeData.unresolvedTickets.count}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Active Agents
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {safeData.topAgents.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ticket Volume Trends */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Ticket Volume Trends
          </h3>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {getDateRangeLabel()}
          </div>
        </div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart 
              key={`ticket-volume-${dateRangeOption}-${selectedAgent}-${selectedCategory}`} 
              data={safeData.ticketVolumeData}
            >
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="date" 
                className="text-xs fill-gray-600 dark:fill-gray-400"
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis className="text-xs fill-gray-600 dark:fill-gray-400" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgb(31 41 55)', 
                  border: 'none', 
                  borderRadius: '8px',
                  color: 'white'
                }}
                labelFormatter={(label) => `Date: ${label}`}
                formatter={(value: number) => [value, 'Tickets']}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="tickets" 
                stroke="#3B82F6" 
                strokeWidth={2}
                dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#3B82F6', strokeWidth: 2, fill: '#3B82F6' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Service Workflow Volume Trends */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Service Workflow Volume
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Service workflows created and completed over time
            </p>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {getDateRangeLabel()}
          </div>
        </div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart 
              key={`service-workflow-${dateRangeOption}-${selectedAgent}-${selectedCategory}`} 
              data={safeData.serviceWorkflowVolumeData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="displayPeriod" 
                className="text-xs fill-gray-600 dark:fill-gray-400"
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis className="text-xs fill-gray-600 dark:fill-gray-400" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgb(31 41 55)', 
                  border: 'none', 
                  borderRadius: '8px',
                  color: 'white'
                }}
                labelFormatter={(label) => `Period: ${label}`}
                formatter={(value: number, name: string) => {
                  if (name === 'total') return [value, 'Total Workflows'];
                  if (name === 'completed') return [value, 'Completed'];
                  if (name === 'in_progress') return [value, 'In Progress'];
                  if (name === 'cancelled') return [value, 'Cancelled'];
                  return [value, name];
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="total" 
                stroke="#3B82F6" 
                strokeWidth={2}
                dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#3B82F6', strokeWidth: 2, fill: '#3B82F6' }}
                name="Total Workflows"
              />
              <Line 
                type="monotone" 
                dataKey="completed" 
                stroke="#10B981" 
                strokeWidth={2}
                dot={{ fill: '#10B981', strokeWidth: 2, r: 3 }}
                activeDot={{ r: 5, stroke: '#10B981', strokeWidth: 2, fill: '#10B981' }}
                name="Completed"
              />
              <Line 
                type="monotone" 
                dataKey="in_progress" 
                stroke="#F59E0B" 
                strokeWidth={2}
                dot={{ fill: '#F59E0B', strokeWidth: 2, r: 3 }}
                activeDot={{ r: 5, stroke: '#F59E0B', strokeWidth: 2, fill: '#F59E0B' }}
                name="In Progress"
              />
              <Line 
                type="monotone" 
                dataKey="cancelled" 
                stroke="#EF4444" 
                strokeWidth={1}
                strokeDasharray="5 5"
                dot={{ fill: '#EF4444', strokeWidth: 2, r: 2 }}
                activeDot={{ r: 4, stroke: '#EF4444', strokeWidth: 2, fill: '#EF4444' }}
                name="Cancelled"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Service Workflow Summary Stats */}
        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-center">
            <div className="text-blue-600 dark:text-blue-400 font-medium">Total</div>
            <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {data.serviceWorkflowVolumeData?.summary?.totalWorkflows || 0}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">workflows</div>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-center">
            <div className="text-green-600 dark:text-green-400 font-medium">Completed</div>
            <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {data.serviceWorkflowVolumeData?.summary?.completed || 0}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              {data.serviceWorkflowVolumeData?.summary?.completionRate || 0}% rate
            </div>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg text-center">
            <div className="text-orange-600 dark:text-orange-400 font-medium">In Progress</div>
            <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {data.serviceWorkflowVolumeData?.summary?.inProgress || 0}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">active</div>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg text-center">
            <div className="text-red-600 dark:text-red-400 font-medium">Cancelled</div>
            <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {data.serviceWorkflowVolumeData?.summary?.cancelled || 0}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">stopped</div>
          </div>
        </div>
      </div>

      {/* Distribution Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {/* Category Distribution */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Category Distribution
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={safeData.categoryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {safeData.categoryData.map((entry: CategoryDataItem, index: number) => (
                    <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Device Model Distribution */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Device Model Distribution
          </h3>
          <div className="h-80">
            {(() => {
              console.log('🔍 DEVICE MODEL CHART: safeData.deviceModelData:', safeData.deviceModelData);
              console.log('🔍 DEVICE MODEL CHART: length check:', safeData.deviceModelData && safeData.deviceModelData.length > 0);
              return safeData.deviceModelData && safeData.deviceModelData.length > 0;
            })() ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={safeData.deviceModelData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {safeData.deviceModelData.map((entry: DeviceModelDataItem, index: number) => (
                      <Cell key={`device-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgb(31 41 55)', 
                      border: 'none', 
                      borderRadius: '8px',
                      color: 'white'
                    }}
                    formatter={(value, name, props) => {
                      const total = safeData.deviceModelData.reduce((sum: number, item: DeviceModelDataItem) => sum + item.value, 0);
                      const percentage = total > 0 ? ((value as number / total) * 100).toFixed(1) : '0.0';
                      return [`${value} tickets (${percentage}%)`, 'Count'];
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500 dark:text-gray-400">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p className="mt-2 text-sm">No device model data available</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Hourly Ticket Creation */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Ticket Creation by Hour
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            24-hour distribution showing when tickets are created most often
          </p>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={safeData.hourlyTicketData.data}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="hourLabel" 
                  className="text-xs fill-gray-600 dark:fill-gray-400"
                  tick={{ fontSize: 10 }}
                  interval={2} // Show every 3rd hour label to avoid crowding
                />
                <YAxis 
                  className="text-xs fill-gray-600 dark:fill-gray-400"
                  tick={{ fontSize: 10 }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgb(31 41 55)', 
                    border: 'none', 
                    borderRadius: '8px',
                    color: 'white'
                  }}
                  labelFormatter={(label) => `Time: ${label}`}
                  formatter={(value: number, name: string, props: any) => {
                    const percentage = props.payload.percentage;
                    return [`${value} tickets (${percentage}%)`, 'Count'];
                  }}
                />
                <Bar 
                  dataKey="count" 
                  fill="#3B82F6"
                  radius={[2, 2, 0, 0]}
                >
                  {safeData.hourlyTicketData.data.map((entry: HourlyTicketDataItem, index: number) => {
                    // Highlight peak hours with different colors
                    let fillColor = '#3B82F6'; // Default blue
                    if (entry.hour >= 9 && entry.hour <= 17) {
                      fillColor = '#10B981'; // Green for business hours
                    }
                    if (entry.hour === safeData.hourlyTicketData.statistics.peakHour.hour) {
                      fillColor = '#F59E0B'; // Orange for peak hour
                    }
                    return <Cell key={`cell-${index}`} fill={fillColor} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Statistics Summary */}
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
              <div className="text-orange-600 dark:text-orange-400 font-medium">
                Peak Hour
              </div>
              <div className="text-gray-900 dark:text-gray-100 font-bold">
                {safeData.hourlyTicketData.statistics.peakHour.label}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {safeData.hourlyTicketData.statistics.peakHour.count} tickets
              </div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
              <div className="text-green-600 dark:text-green-400 font-medium">
                Business Hours
              </div>
              <div className="text-gray-900 dark:text-gray-100 font-bold">
                {safeData.hourlyTicketData.statistics.businessHours.percentage}%
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                of all tickets
              </div>
            </div>
          </div>
        </div>

        {/* Top Replaced Components */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Top Replaced Components
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Most frequently replaced parts in service workflow step 6
          </p>
          <div className="h-80">
            {safeData.topReplacedComponentsData.data.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={safeData.topReplacedComponentsData.data.slice(0, 10)}
                  layout="horizontal"
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis 
                    type="number"
                    className="text-xs fill-gray-600 dark:fill-gray-400"
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis 
                    type="category"
                    dataKey="partNumber"
                    className="text-xs fill-gray-600 dark:fill-gray-400"
                    tick={{ fontSize: 10 }}
                    width={80}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgb(31 41 55)', 
                      border: 'none', 
                      borderRadius: '8px',
                      color: 'white'
                    }}
                    labelFormatter={(label) => `Part: ${label}`}
                    formatter={(value: number, name: string, props: any) => {
                      const item = props.payload;
                      return [
                        <div key="tooltip">
                          <div>{value} times used</div>
                          <div className="text-xs opacity-80">{item.itemName}</div>
                          <div className="text-xs opacity-60">{item.category}</div>
                          <div className="text-xs opacity-60">{item.workflowCount} workflows</div>
                        </div>,
                        'Usage Count'
                      ];
                    }}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="#8B5CF6"
                    radius={[0, 4, 4, 0]}
                  >
                    {safeData.topReplacedComponentsData.data.slice(0, 10).map((entry: TopReplacedComponentDataItem, index: number) => {
                      // Color by category
                      let fillColor = '#8B5CF6'; // Default purple
                      if (entry.category === 'Service') {
                        fillColor = '#3B82F6'; // Blue for service
                      } else if (entry.category === 'Raw Material') {
                        fillColor = '#10B981'; // Green for raw materials
                      } else if (entry.category === 'Software') {
                        fillColor = '#F59E0B'; // Orange for software
                      }
                      return <Cell key={`cell-${index}`} fill={fillColor} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500 dark:text-gray-400">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="mt-2 text-sm">No component replacement data</p>
                  <p className="text-xs mt-1">Complete service workflows to see data</p>
                </div>
              </div>
            )}
          </div>

          {/* Component Summary Stats */}
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg text-center">
              <div className="text-purple-600 dark:text-purple-400 font-medium">Total Parts</div>
              <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {safeData.topReplacedComponentsData.summary.totalPartsReplaced}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">replaced</div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-center">
              <div className="text-blue-600 dark:text-blue-400 font-medium">Unique Parts</div>
              <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {safeData.topReplacedComponentsData.summary.uniqueParts}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">different types</div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-center">
              <div className="text-green-600 dark:text-green-400 font-medium">Workflows</div>
              <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {safeData.topReplacedComponentsData.summary.totalWorkflows}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">with parts</div>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg text-center">
              <div className="text-orange-600 dark:text-orange-400 font-medium">Avg/Workflow</div>
              <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {safeData.topReplacedComponentsData.summary.averagePartsPerWorkflow}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">parts each</div>
            </div>
          </div>
        </div>
      </div>

      {/* Geography and Top Agents */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Geography */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Tickets by Location
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={(() => {
                    const total = safeData.geographyData.reduce((sum: number, item: GeographyDataItem) => sum + item.count, 0);
                    return safeData.geographyData.map((item: GeographyDataItem, index: number) => ({
                      ...item,
                      percentage: total > 0 ? ((item.count / total) * 100).toFixed(1) : '0.0'
                    }));
                  })()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ country, percentage }) => `${country}: ${percentage}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {safeData.geographyData.map((entry: GeographyDataItem, index: number) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgb(31 41 55)', 
                    border: 'none', 
                    borderRadius: '8px',
                    color: 'white'
                  }}
                  formatter={(value, name, props) => {
                    const total = safeData.geographyData.reduce((sum: number, item: GeographyDataItem) => sum + item.count, 0);
                    const percentage = total > 0 ? ((value as number / total) * 100).toFixed(1) : '0.0';
                    return [`${value} tickets (${percentage}%)`, 'Count'];
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Agents */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Top Performing Agents
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Ranked by fastest average resolution time (opening to closure)
          </p>
          <div className="space-y-4">
            {safeData.topAgents.length > 0 ? (
              safeData.topAgents.slice(0, 5).map((agent: { id: string; name: string; ticketsResolved: number; avgResolutionTime: number; totalActiveTime: number }, index: number) => (
                <div key={agent.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${
                      index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-500' : 'bg-blue-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {agent.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {agent.ticketsResolved} tickets resolved
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatTime(agent.avgResolutionTime)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      avg close time
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">No agent data available</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Check console for debugging information</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Agent Activity and Resolution Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Agent Activity Time */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Agent Activity Time
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Average logged-in hours per day (last 30 days)
          </p>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={safeData.agentActivityData}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="name" 
                  className="text-xs fill-gray-600 dark:fill-gray-400"
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis className="text-xs fill-gray-600 dark:fill-gray-400" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgb(31 41 55)', 
                    border: 'none', 
                    borderRadius: '8px',
                    color: 'white'
                  }}
                  formatter={(value) => [formatDuration(value as number), 'Avg Hours/Day']}
                />
                <Bar dataKey="hoursWorked" fill="#8B5CF6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Resolution Time Metrics */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Resolution Time Analysis
          </h3>
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {formatTime(safeData.resolutionMetrics.minResolutionTime)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Fastest</p>
              </div>
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {formatTime(safeData.resolutionMetrics.avgResolutionTime)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Average</p>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {formatTime(safeData.resolutionMetrics.maxResolutionTime)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Slowest</p>
              </div>
            </div>
            
            {/* Unresolved Tickets Details */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                Unresolved Tickets Overview
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                  <p className="text-xl font-bold text-orange-600 dark:text-orange-400">
                    {safeData.unresolvedTickets.overdueCount}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Overdue</p>
                </div>
                <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
                    {safeData.unresolvedTickets.avgAge}d
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Avg Age</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Insights; 